import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimCreateWorkOrderChangeMutation } from 'gql/graphql';
import { WorkOrderChangeStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import type { TActionCommonInterface } from 'utils/types';

import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { bimCreateWorkOrderChangeGql } from '../bimWorkOrderChangeCreateOrEditQueries';

export const useCreateWorkOrderChange = () => {
    const { id: projectId, orderId } = useParams();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const sendGpnAnalyticsData = useCallback(
        (data: TActionCommonInterface<BimCreateWorkOrderChangeMutation>) => {
            const { bimCreateWorkOrderChange } = data?.payload ?? {};

            sendGpnAnalyticsEvent({
                componentId: 'rim_IZM file_saved',
                projectId,
                contractId: projectData?.contractId,
                orderId: Number(orderId),
                projectName: projectData?.name,
                projectType: projectData?.contractType,
                projectDO: projectData?.subsidiary?.name,
                methodology: projectData?.methodology,
                orderChangeStatus: WorkOrderChangeStatusEnum.OnFormation,
                orderChangeId: bimCreateWorkOrderChange?.data?.id,
            });
        },
        [
            orderId,
            projectData?.contractId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            sendGpnAnalyticsEvent,
        ],
    );

    const [resetCreatingData] = useResetStoreData({ requestId: 'bimCreateWorkOrderChange' });

    const [
        bimCreateWorkOrderChangeData,
        bimCreateWorkOrderChangeStatus,
        bimCreateWorkOrderError,
        bimCreateWorkOrderChangeRequest,
    ] = useRequest({
        requestId: 'bimCreateWorkOrderChange',
        query: bimCreateWorkOrderChangeGql,
        withEffect: false,
        dependenciesUpdater: sendGpnAnalyticsData,
    });

    const isCreatingInProgress = useMemo(
        () => bimCreateWorkOrderChangeStatus === RequestStatus.FETCHING,
        [bimCreateWorkOrderChangeStatus],
    );

    return useMemo(
        () => ({
            creatingData: bimCreateWorkOrderChangeData?.bimCreateWorkOrderChange,
            creatingStatus: bimCreateWorkOrderChangeStatus,
            creatingError: bimCreateWorkOrderError,
            creatingRequest: bimCreateWorkOrderChangeRequest,
            resetCreatingData,
            isCreatingInProgress,
        }),
        [
            bimCreateWorkOrderChangeData?.bimCreateWorkOrderChange,
            bimCreateWorkOrderChangeRequest,
            bimCreateWorkOrderChangeStatus,
            bimCreateWorkOrderError,
            isCreatingInProgress,
            resetCreatingData,
        ],
    );
};
